<template>
  <div class="print-mode-detail">
    <div class="columns is-multiline">
      <div
        class="
          column
          is-full is-flex is-justify-content-space-between is-align-items-center
        "
      >
        <p class="title is-3 is-flex is-align-items-center mb-0">
          {{ printMode.name }}
        </p>
      </div>
      <div class="column is-full">
        <div class="media">
          <div class="media-left">
            <b-image
              class="image is-128x128"
              :src="`${thumbnailBaseUrl}${printMode.surfaceFinishes[0].colors[0].thumbnail}`"
              :alt="`${printMode.name} ${printMode.surfaceFinishes[0].colors[0].name}`"
            ></b-image>
          </div>
          <div class="media-content">
            <div v-if="printMode.printModeMaterial" class="mb-5">
              <div>
                <p>{{ $t("printMode.material") }}:</p>
                <strong class="is-size-4">{{
                  printMode.printModeMaterial["name"]
                }}</strong>
              </div>
              <div>
                <p>{{ $t("printMode.method") }}:</p>
                <strong class="is-size-4">{{
                  printMode.printModeMethod["name"]
                }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="column is-full">
        <div class="content">
          <p class="title is-5 has-text-weight-bold mb-1">
            {{ $t("printMode.description") }}
          </p>
          <p>{{ printMode.features.description }}</p>
          <p class="title is-5 has-text-weight-bold mb-1">
            {{ $t("printMode.surfaceQuality") }}
          </p>
          <p>{{ printMode.features.surfaceQuality }}</p>
          <p class="title is-5 has-text-weight-bold mb-1">
            {{ $t("printMode.applications") }}
          </p>
          <p>{{ printMode.features.applications }}</p>
        </div>
        <div class="colors">
          <p class="title is-5 mb-1 has-text-weight-bold">
            {{ $t("printMode.availableSurfaceFinishes") }}
          </p>
          <div class="color-panel py-3">
            <span
              class="tag print-item__color mr-3 mb-3 is-white"
              v-for="finish in printMode.surfaceFinishes"
              :key="`print_mode_${printMode._id}__color_${finish._id}`"
              >{{ finish.properties.name }}</span
            >
          </div>
        </div>
        <div class="divider has-background-grey my-5" style="width: 100%"></div>
        <div class="columns is-multiline">
          <div class="column is-half">
            <p class="title is-5 has-text-weight-bold mb-1">
              {{ $t("printMode.technicalFeaturesLabels.maxDimensions") }}
            </p>
            <p>
              {{ printMode.features.maxDimensions.X }} x
              {{ printMode.features.maxDimensions.Y }} x
              {{ printMode.features.maxDimensions.Z }} mm
            </p>
          </div>
          <div class="column is-half">
            <p class="title is-5 has-text-weight-bold mb-1">
              {{ $t("printMode.technicalFeaturesLabels.minFeatureSize") }}
            </p>
            <p>{{ printMode.features.minFeatureSize }} mm</p>
          </div>
          <div class="column is-half">
            <p class="title is-5 has-text-weight-bold mb-1">
              {{ $t("printMode.technicalFeaturesLabels.resolutionXY") }}
            </p>
            <p>{{ printMode.features.resolutionXY }} mm</p>
          </div>
          <div class="column is-half">
            <p class="title is-5 has-text-weight-bold mb-1">
              {{ $t("printMode.technicalFeaturesLabels.resolutionZ") }}
            </p>
            <p>{{ printMode.features.resolutionZ }} mm</p>
          </div>
        </div>
      </div>
      <div class="column is-full">
        <div class="divider has-background-grey my-5" style="width: 100%"></div>
        <div class="print-mode-specs">
          <p class="title is-4 mb-1">{{ $t("printMode.technicalFeatures") }}</p>
          <div class="columns is-multiline">
            <template
              v-for="(ranking, index) in Object.entries(
                printMode.features.ranking
              )"
            >
              <div
                :key="`printmode_${printMode._id}_ranking_${index}_B`"
                class="column is-one-third is-flex is-align-items-center"
              >
                <p class="has-text-weight-bold">
                  {{ $t(`printMode.rankingLabels.${ranking[0]}`) }}
                </p>
              </div>
              <div
                :key="`printmode_${printMode._id}_ranking_${index}_A`"
                class="column is-two-thirds"
              >
                <b-slider
                  size="is-small"
                  :min="0"
                  :max="8"
                  :value="ranking[1]"
                  disabled
                  rounded
                >
                  <template v-for="val in 7">
                    <b-slider-tick :value="val" :key="val">{{
                      val
                    }}</b-slider-tick>
                  </template>
                </b-slider>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="js">
import {mapGetters} from "vuex";

export default {
  data() {
    return {
      thumbnailBaseUrl: process.env.VUE_APP_FULFILLMENT_API_BASE_URL,
    }
  },
  computed: {
    ...mapGetters(['printModeById']),
    printMode() {
      return this.printModeById(this.printModeId);
    }
  },
  props: {
    printModeId: {
      type: String,
      required: true,
    }
  },
  methods: {
    findColorInvert(hex) {
      const r = parseInt(hex[1] + hex[2], 16);
      const g = parseInt(hex[3] + hex[4], 16);
      const b = parseInt(hex[5] + hex[6], 16);
      const brightness = Math.round((r * 299 + g * 587 + b * 114) / 1000);
      return brightness > 125 ? '#000000' : '#ffffff';
    },

    tagStyle(color) {
      return {
        backgroundColor: color.hex,
        color: this.findColorInvert(color.hex),
        boxShadow: '0px 0px 2px grey'
      };
    }
  }
}
</script>

<style lang="scss">
.print-mode-specs {
  .b-slider.is-disabled {
    .b-slider-track {
      cursor: default;
      opacity: 1;
    }

    .b-slider-thumb-wrapper {
      cursor: default;
    }
  }
}
</style>
